/**
 * Student + parent modal. Replaced geocoded pickup block with mandatory indicative address
 * (`pickup_address`) and optional searchable stop select (`assigned_stop_id`). No pickup lat/lng.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, MapPin, Phone, Mail, GraduationCap, Save, Users, UserPlus, Search, ChevronDown } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

const StudentParentFormModal = ({ isOpen, onClose, onSubmit, student = null, mode = 'create', loading = false }) => {
  const [activeTab, setActiveTab] = useState('student');
  const [parentSource, setParentSource] = useState('new'); // 'new' | 'existing'
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [parentsList, setParentsList] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  const [parentDropdownOpen, setParentDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    // Étudiant
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'Garçon',
    grade: '',
    status: 'active',
    pickupAddress: '',
    assignedStopId: '',

    // Parent — mêmes champs que la popup "Modifier un parent"
    parentFirstName: '',
    parentLastName: '',
    parentDateOfBirth: '',
    parentStatus: 'active',
    parentEmail: '',
    parentPhone: '',
    parentEmergencyContactName: '',
    parentEmergencyContact: '',
    parentRelation: 'Père',
    parentAddress: '',
    parentId: null,
  });

  const [errors, setErrors] = useState({});
  const [stopsList, setStopsList] = useState([]);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [stopSearch, setStopSearch] = useState('');
  const [stopDropdownOpen, setStopDropdownOpen] = useState(false);
  // Initialisation du formulaire
  useEffect(() => {
    if (isOpen) {
      if (student && mode === 'edit') {
        // Mode édition : pré-remplir les données
        console.group('📝 DEBUG MODAL ÉDITION');
        logger.debug('1. Données brutes reçues (student):', student);
        logger.debug('2. Structure parents:', {
          parent_obj: student.parent,
          parents_array: student.parents,
          direct_fields: {
            fn: student.parent_first_name,
            ln: student.parent_last_name,
          },
        });

        setFormData({
          firstName: student.first_name || student.firstName || '',
          lastName: student.last_name || student.lastName || '',
          dateOfBirth: (student.date_of_birth || student.dateOfBirth || '').split('T')[0],
          gender:
            (student.gender === 'male' ? 'Garçon' : student.gender === 'female' ? 'Fille' : student.gender) || 'Garçon',
          grade: (() => {
            // Combiner grade_level et class_name s'ils existent tous les deux
            const gradeLevel = student.grade_level || student.grade || '';
            const className = student.class_name || student.class || '';
            if (gradeLevel && className) {
              return `${gradeLevel} ${className}`.trim();
            }
            return gradeLevel || '';
          })(),
          status: student.status || 'active',
          pickupAddress: student.pickup_address || '',
          assignedStopId: student.assigned_stop_id || student.assignedStop?.id || '',

          // Données parent (si disponibles)
          // Vérification approfondie des objets imbriqués (parent, parents[0] ou parentsData[0])
          parentFirstName:
            student.parent_first_name ||
            student.parentFirstName ||
            (student.parent && (student.parent.first_name || student.parent.firstName)) ||
            (student.parents &&
              student.parents[0] &&
              (student.parents[0].first_name || student.parents[0].firstName)) ||
            (student.parentsData &&
              student.parentsData[0] &&
              (student.parentsData[0].first_name || student.parentsData[0].firstName)) ||
            '',

          parentLastName:
            student.parent_last_name ||
            student.parentLastName ||
            (student.parent && (student.parent.last_name || student.parent.lastName)) ||
            (student.parents && student.parents[0] && (student.parents[0].last_name || student.parents[0].lastName)) ||
            (student.parentsData &&
              student.parentsData[0] &&
              (student.parentsData[0].last_name || student.parentsData[0].lastName)) ||
            '',

          parentEmail:
            student.parent_email ||
            student.parentEmail ||
            (student.parent && student.parent.email) ||
            (student.parents && student.parents[0] && student.parents[0].email) ||
            (student.parentsData && student.parentsData[0] && student.parentsData[0].email) ||
            '',

          parentPhone:
            student.parent_phone ||
            student.parentPhone ||
            (student.parent && student.parent.phone) ||
            (student.parents && student.parents[0] && student.parents[0].phone) ||
            (student.parentsData && student.parentsData[0] && student.parentsData[0].phone) ||
            '',

          parentRelation:
            student.parent_relation ||
            student.parentRelation ||
            (student.parent && student.parent.relation) ||
            (student.parents && student.parents[0] && student.parents[0].relation) ||
            (student.parentsData && student.parentsData[0] && student.parentsData[0].relation) ||
            'Père',

          parentAddress:
            student.parent_address ||
            student.parentAddress ||
            (student.parent && student.parent.address) ||
            (student.parents && student.parents[0] && student.parents[0].address) ||
            (student.parentsData && student.parentsData[0] && student.parentsData[0].address) ||
            student.pickup_address ||
            '',

          parentId:
            student.parent_id ||
            student.parentId ||
            (student.parent && student.parent.id) ||
            (student.parents && student.parents[0] && student.parents[0].id) ||
            (student.parentsData && student.parentsData[0] && student.parentsData[0].id) ||
            null,
          // Champs alignés sur "Modifier un parent"
          parentDateOfBirth:
            (
              student.parent?.date_of_birth ||
              student.parent?.dateOfBirth ||
              (student.parents && student.parents[0]?.date_of_birth) ||
              ''
            )
              .toString()
              .split('T')[0] || '',
          parentStatus: student.parent?.status || (student.parents && student.parents[0]?.status) || 'active',
          parentEmergencyContactName:
            student.parent?.emergency_contact_name ||
            (student.parents && student.parents[0]?.emergency_contact_name) ||
            '',
          parentEmergencyContact:
            student.parent?.emergency_contact_phone ||
            student.parent?.emergency_contact_phone ||
            (student.parents && student.parents[0]?.emergency_contact_phone) ||
            '',
        });

        logger.debug('3. Données mappées (formData):', {
          firstName: student.first_name || student.firstName || '',
          lastName: student.last_name || student.lastName || '',
          parentFirstName:
            student.parent_first_name ||
            student.parentFirstName ||
            (student.parent && (student.parent.first_name || student.parent.firstName)) ||
            (student.parents &&
              student.parents[0] &&
              (student.parents[0].first_name || student.parents[0].firstName)) ||
            '',
          parentLastName:
            student.parent_last_name ||
            student.parentLastName ||
            (student.parent && (student.parent.last_name || student.parent.lastName)) ||
            (student.parents && student.parents[0] && (student.parents[0].last_name || student.parents[0].lastName)) ||
            '',
        });
        console.groupEnd();
      } else {
        // Mode création : réinitialiser
        setFormData({
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: 'Garçon',
          grade: '',
          status: 'active',
          pickupAddress: '',
          assignedStopId: '',
          parentFirstName: '',
          parentLastName: '',
          parentDateOfBirth: '',
          parentStatus: 'active',
          parentEmail: '',
          parentPhone: '',
          parentEmergencyContactName: '',
          parentEmergencyContact: '',
          parentRelation: 'Père',
          parentAddress: '',
          parentId: null,
        });
      }
      // En mode édition, forcer l'onglet étudiant (pas d'onglet parent)
      setActiveTab(mode === 'edit' ? 'student' : 'student');
      setErrors({});
      setParentSource(student && (student.parent_id || student.parentId || student.parent?.id) ? 'existing' : 'new');
      setSelectedParentId(student?.parent_id || student?.parentId || student?.parent?.id || null);
      setParentSearch('');
      setParentDropdownOpen(false);
      setStopSearch('');
      setStopDropdownOpen(false);
      if (isOpen) {
        loadParentsList();
        loadStopsList();
      }
    }
  }, [isOpen, student, mode]);

  const loadStopsList = async () => {
    setStopsLoading(true);
    try {
      // Dropdown d'assignation élève: uniquement les arrêts actifs.
      const res = await unifiedApi.stops.getAllUnlimited({
        status: 'active',
      });
      if (res.success && Array.isArray(res.data)) {
        setStopsList(res.data.filter((s) => s?.status === 'active'));
      } else {
        setStopsList([]);
      }
    } catch (e) {
      logger.error('Erreur chargement arrêts:', e);
      setStopsList([]);
    } finally {
      setStopsLoading(false);
    }
  };

  const loadParentsList = async () => {
    setParentsLoading(true);
    try {
      const res = (await unifiedApi.parents.getAllUnlimited?.()) ?? unifiedApi.parents.getAll?.({ limit: 500 });
      const list = (res?.data ?? res?.parents ?? []) || [];
      setParentsList(Array.isArray(list) ? list : []);
    } catch (e) {
      logger.error('Erreur chargement liste parents:', e);
      setParentsList([]);
    } finally {
      setParentsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^(\+212|0)[5-7][0-9]{8}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validation Étudiant
    if (!formData.firstName.trim()) newErrors.firstName = 'Le prénom est obligatoire';
    if (!formData.lastName.trim()) newErrors.lastName = 'Le nom est obligatoire';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'La date de naissance est obligatoire';
    if (!formData.pickupAddress.trim()) newErrors.pickupAddress = "L'adresse est obligatoire";

    // Validation Parent : selon choix "existant" ou "nouveau"
    if (parentSource === 'existing') {
      if (!selectedParentId) newErrors.selectedParentId = 'Veuillez sélectionner un parent existant';
    } else {
      if (!formData.parentFirstName.trim()) newErrors.parentFirstName = 'Le prénom du parent est obligatoire';
      if (!formData.parentLastName.trim()) newErrors.parentLastName = 'Le nom du parent est obligatoire';
      if (!formData.parentPhone.trim()) newErrors.parentPhone = 'Le téléphone est obligatoire';
      if (!formData.parentEmail.trim()) newErrors.parentEmail = "L'email est obligatoire";
      if (formData.parentEmail && !emailRegex.test(formData.parentEmail)) {
        newErrors.parentEmail = 'Format email invalide';
      }
      if (formData.parentPhone && !phoneRegex.test(formData.parentPhone.replace(/\s/g, ''))) {
        newErrors.parentPhone = 'Format téléphone invalide (ex: +212 6 12 34 56 78)';
      }
      if (
        formData.parentEmergencyContact &&
        formData.parentEmergencyContact.trim() &&
        !phoneRegex.test(formData.parentEmergencyContact.replace(/\s/g, ''))
      ) {
        newErrors.parentEmergencyContact = 'Format téléphone invalide';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Parser le champ Classe : séparer selon l'espace
      const gradeValue = formData.grade.trim();
      let gradeLevel = '';
      let className = '';
      if (gradeValue.includes(' ')) {
        const parts = gradeValue.split(' ');
        gradeLevel = parts[0] || '';
        className = parts.slice(1).join(' ') || ''; // En cas de plusieurs espaces, prendre tout après le premier
      } else {
        gradeLevel = gradeValue;
        className = '';
      }

      const submissionData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender === 'Garçon' ? 'male' : formData.gender === 'Fille' ? 'female' : formData.gender,
        grade_level: gradeLevel,
        class_name: className,
        status: formData.status,
        pickup_address: formData.pickupAddress,
        assigned_stop_id: formData.assignedStopId || null,
      };

      if (parentSource === 'existing' && selectedParentId) {
        submissionData.parent_id = selectedParentId;
        submissionData.parent = { id: selectedParentId };
      } else {
        submissionData.parent = {
          first_name: formData.parentFirstName,
          last_name: formData.parentLastName,
          email: formData.parentEmail,
          phone: formData.parentPhone,
          relation: formData.parentRelation,
          address: formData.parentAddress || formData.pickupAddress,
          date_of_birth: formData.parentDateOfBirth || undefined,
          status: formData.parentStatus,
          emergency_contact_name: formData.parentEmergencyContactName || undefined,
          emergency_contact_phone: formData.parentEmergencyContact || undefined,
        };
      }

      onSubmit(submissionData);
    } else {
      // Si erreur dans l'onglet parent alors qu'on est sur l'onglet étudiant, changer d'onglet
      if (activeTab === 'student' && (errors.parentFirstName || errors.parentLastName || errors.parentPhone)) {
        // On pourrait changer d'onglet automatiquement, mais pour l'instant on laisse l'utilisateur voir les erreurs
        // setActiveTab('parent');
      }
    }
  };

  if (!isOpen) return null;

  // En mode édition, masquer l'onglet Parent
  const tabs = [
    { id: 'student', label: 'Élève', icon: GraduationCap },
    ...(mode !== 'edit' ? [{ id: 'parent', label: 'Parent', icon: Users }] : []),
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <GraduationCap className="w-6 h-6 text-blue-600" />
                  </div>
                  {mode === 'create' ? 'Nouvel Élève' : "Modifier l'Élève"}
                </h2>
                <p className="text-gray-500 mt-1 ml-12">
                  {mode === 'create'
                    ? 'Ajoutez un nouvel élève et ses informations parentales'
                    : "Modifiez les informations de l'élève et du parent"}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            {tabs.length > 1 && (
              <div className="px-8 border-b border-gray-100 bg-gray-50/50">
                <div className="flex gap-6 overflow-x-auto no-scrollbar">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        relative py-4 px-2 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap focus:outline-none focus-visible:outline-none
                        ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}
                      `}
                    >
                      <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`} />
                      {tab.label}
                      {activeTab === tab.id && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Onglet Élève */}
                {activeTab === 'student' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                              errors.firstName
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-200 focus:border-blue-500'
                            }`}
                            placeholder="Ex: Mehdi"
                          />
                        </div>
                        {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                            errors.lastName
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-blue-500'
                          }`}
                          placeholder="Ex: Chakir"
                        />
                        {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Date de naissance <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                          className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                            errors.dateOfBirth
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-blue-500'
                          }`}
                        />
                        {errors.dateOfBirth && <p className="text-xs text-red-500 mt-1">{errors.dateOfBirth}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Genre</label>
                        <select
                          value={formData.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                          <option value="Garçon">Garçon</option>
                          <option value="Fille">Fille</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Classe{' '}
                          <span className="text-gray-500 font-normal">(ex: "CE2 A", niveau "CE2" et classe "A")</span>
                        </label>
                        <div className="relative">
                          <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.grade}
                            onChange={(e) => handleInputChange('grade', e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            placeholder="CE2 A"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Statut</label>
                        <select
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                          <option value="active">Actif</option>
                          <option value="inactive">Inactif</option>
                        </select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">
                          Adresse (à titre indicatif) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.pickupAddress}
                            onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                            className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                              errors.pickupAddress
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-200 focus:border-blue-500'
                            }`}
                            placeholder="Ex: Rue Al Jazair, Hay Riad, Rabat"
                          />
                        </div>
                        {errors.pickupAddress && <p className="text-xs text-red-500 mt-1">{errors.pickupAddress}</p>}
                      </div>
                    </div>

                    {/* Prise en charge (arrêt) */}
                    <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-blue-900">Prise en charge (optionnel)</h3>
                        <label className="text-sm font-medium text-gray-700">Sélectionner un arrêt existant</label>
                        {stopsLoading ? (
                          <div className="flex items-center gap-2 text-gray-500 py-2">
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                            <span>Chargement des arrêts...</span>
                          </div>
                        ) : (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setStopDropdownOpen(!stopDropdownOpen)}
                              className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white border rounded-xl text-left text-sm transition-colors border-gray-200 hover:border-gray-300 ${stopDropdownOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}`}
                            >
                              <span className={formData.assignedStopId ? 'text-gray-900' : 'text-gray-500'}>
                                {formData.assignedStopId
                                  ? (() => {
                                      const s = stopsList.find((x) => x.id === formData.assignedStopId);
                                      return s ? s.stop_name || s.name || 'Arrêt' : 'Arrêt sélectionné';
                                    })()
                                  : 'Liste des arrêts existants'}
                              </span>
                              <ChevronDown
                                className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${stopDropdownOpen ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {formData.assignedStopId && (
                              <button
                                type="button"
                                onClick={() => handleInputChange('assignedStopId', '')}
                                className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                Retirer la sélection
                              </button>
                            )}
                            {stopDropdownOpen && (
                              <div
                                className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                      type="text"
                                      value={stopSearch}
                                      onChange={(e) => setStopSearch(e.target.value)}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      placeholder="Rechercher un arrêt..."
                                      className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                      autoFocus
                                    />
                                  </div>
                                </div>
                                <ul className="max-h-52 overflow-auto py-1">
                                  {(() => {
                                    const q = stopSearch.trim().toLowerCase();
                                    const filtered = !q
                                      ? stopsList
                                      : stopsList.filter((s) => {
                                          const name = (s.stop_name || s.name || '').toLowerCase();
                                          const addr = (s.address || '').toLowerCase();
                                          return name.includes(q) || addr.includes(q);
                                        });
                                    return filtered.length === 0 ? (
                                      <li className="px-4 py-3 text-sm text-gray-500">Aucun arrêt trouvé</li>
                                    ) : (
                                      filtered.map((s) => {
                                        const label = s.stop_name || s.name || 'Arrêt';
                                        return (
                                          <li key={s.id}>
                                            <button
                                              type="button"
                                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${formData.assignedStopId === s.id ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`}
                                              onClick={() => {
                                                handleInputChange('assignedStopId', s.id);
                                                setStopDropdownOpen(false);
                                                setStopSearch('');
                                              }}
                                            >
                                              {label}
                                            </button>
                                          </li>
                                        );
                                      })
                                    );
                                  })()}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Si votre arrêt n&apos;existe pas dans la liste, vous pouvez le créer dans la page{' '}
                          <span className="font-medium text-gray-700">Gestion des arrêts</span>.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Onglet Parent — choix parent existant ou nouveau + champs */}
                {activeTab === 'parent' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    {/* Choix : associer un parent existant ou créer un nouveau */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="parentSource"
                            checked={parentSource === 'new'}
                            onChange={() => {
                              setParentSource('new');
                              setSelectedParentId(null);
                              setErrors((prev) => ({
                                ...prev,
                                selectedParentId: null,
                              }));
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <UserPlus className="w-4 h-4 text-gray-500" />
                          <span>Créer un nouveau parent</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="parentSource"
                            checked={parentSource === 'existing'}
                            onChange={() => {
                              setParentSource('existing');
                              setErrors((prev) => ({
                                ...prev,
                                parentFirstName: null,
                                parentLastName: null,
                                parentPhone: null,
                                parentEmail: null,
                              }));
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <Search className="w-4 h-4 text-gray-500" />
                          <span>Associer un parent existant</span>
                        </label>
                      </div>
                    </div>

                    {/* Select parent existant — au même niveau que le formulaire */}
                    {parentSource === 'existing' && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Sélectionner le parent <span className="text-red-500">*</span>
                        </label>
                        {parentsLoading ? (
                          <div className="flex items-center gap-2 text-gray-500 py-2">
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                            <span>Chargement des parents...</span>
                          </div>
                        ) : (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setParentDropdownOpen(!parentDropdownOpen)}
                              className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white border rounded-xl text-left text-sm transition-colors ${
                                errors.selectedParentId ? 'border-red-300' : 'border-gray-200 hover:border-gray-300'
                              } ${parentDropdownOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}`}
                            >
                              <span className={selectedParentId ? 'text-gray-900' : 'text-gray-500'}>
                                {selectedParentId
                                  ? (() => {
                                      const p = parentsList.find((x) => x.id === selectedParentId);
                                      return p
                                        ? `${(p.first_name || p.firstName || '').trim()} ${(p.last_name || p.lastName || '').trim()}`.trim() +
                                            ` — ${p.email || p.phone || 'Sans contact'}`
                                        : 'Choisir un parent';
                                    })()
                                  : 'Choisir un parent'}
                              </span>
                              <ChevronDown
                                className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${parentDropdownOpen ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {parentDropdownOpen && (
                              <div
                                className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                      type="text"
                                      value={parentSearch}
                                      onChange={(e) => setParentSearch(e.target.value)}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      placeholder="Rechercher par nom ou email..."
                                      className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                      autoFocus
                                    />
                                  </div>
                                </div>
                                <ul className="max-h-52 overflow-auto py-1">
                                  {(() => {
                                    const q = parentSearch.trim().toLowerCase();
                                    const filtered = !q
                                      ? parentsList
                                      : parentsList.filter((p) => {
                                          const fn = (p.first_name || p.firstName || '').toLowerCase();
                                          const ln = (p.last_name || p.lastName || '').toLowerCase();
                                          const em = (p.email || '').toLowerCase();
                                          return (
                                            fn.includes(q) ||
                                            ln.includes(q) ||
                                            em.includes(q) ||
                                            `${fn} ${ln}`.trim().includes(q) ||
                                            `${ln} ${fn}`.trim().includes(q)
                                          );
                                        });
                                    return filtered.length === 0 ? (
                                      <li className="px-4 py-3 text-sm text-gray-500">Aucun parent trouvé</li>
                                    ) : (
                                      filtered.map((p) => {
                                        const label =
                                          `${(p.first_name || p.firstName || '').trim()} ${(p.last_name || p.lastName || '').trim()}`.trim() +
                                          ` — ${p.email || p.phone || 'Sans contact'}`;
                                        return (
                                          <li
                                            key={p.id}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              setSelectedParentId(p.id);
                                              setParentSearch('');
                                              setParentDropdownOpen(false);
                                              if (errors.selectedParentId)
                                                setErrors((prev) => ({
                                                  ...prev,
                                                  selectedParentId: null,
                                                }));
                                            }}
                                            className={`px-4 py-2.5 cursor-pointer text-sm hover:bg-blue-50 ${selectedParentId === p.id ? 'bg-blue-50 text-blue-700' : 'text-gray-800'}`}
                                          >
                                            {label}
                                          </li>
                                        );
                                      })
                                    );
                                  })()}
                                </ul>
                              </div>
                            )}
                            {parentDropdownOpen && (
                              <div
                                className="fixed inset-0 z-10"
                                aria-hidden
                                onClick={() => setParentDropdownOpen(false)}
                              />
                            )}
                            {errors.selectedParentId && (
                              <p className="text-xs text-red-500 mt-1">{errors.selectedParentId}</p>
                            )}
                          </div>
                        )}
                        {parentsList.length === 0 && !parentsLoading && (
                          <p className="text-sm text-amber-600 mt-2">
                            Aucun parent enregistré. Créez d'abord un parent ou choisissez « Créer un nouveau parent ».
                          </p>
                        )}
                      </div>
                    )}

                    {parentSource === 'new' && (
                      <>
                        {/* Ligne 1 : Prénom et Nom */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Prénom <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                value={formData.parentFirstName}
                                onChange={(e) => handleInputChange('parentFirstName', e.target.value)}
                                className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                                  errors.parentFirstName
                                    ? 'border-red-300 focus:border-red-500'
                                    : 'border-gray-200 focus:border-blue-500'
                                }`}
                                placeholder="Prénom du parent"
                              />
                            </div>
                            {errors.parentFirstName && (
                              <p className="text-xs text-red-500 mt-1">{errors.parentFirstName}</p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Nom <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.parentLastName}
                              onChange={(e) => handleInputChange('parentLastName', e.target.value)}
                              className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                                errors.parentLastName
                                  ? 'border-red-300 focus:border-red-500'
                                  : 'border-gray-200 focus:border-blue-500'
                              }`}
                              placeholder="Nom de famille"
                            />
                            {errors.parentLastName && (
                              <p className="text-xs text-red-500 mt-1">{errors.parentLastName}</p>
                            )}
                          </div>
                        </div>

                        {/* Ligne 2 : Email et Téléphone principal */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="email"
                                value={formData.parentEmail}
                                onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                                className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                                  errors.parentEmail
                                    ? 'border-red-300 focus:border-red-500'
                                    : 'border-gray-200 focus:border-blue-500'
                                }`}
                                placeholder="parent@email.com"
                              />
                            </div>
                            {errors.parentEmail && <p className="text-xs text-red-500 mt-1">{errors.parentEmail}</p>}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Téléphone principal <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="tel"
                                value={formData.parentPhone}
                                onChange={(e) => handleInputChange('parentPhone', e.target.value)}
                                className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                                  errors.parentPhone
                                    ? 'border-red-300 focus:border-red-500'
                                    : 'border-gray-200 focus:border-blue-500'
                                }`}
                                placeholder="+212 6 12 34 56 78"
                              />
                            </div>
                            {errors.parentPhone && <p className="text-xs text-red-500 mt-1">{errors.parentPhone}</p>}
                          </div>
                        </div>

                        {/* Ligne 3 : Contact d'urgence (nom) et Téléphone d'urgence */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Contact d'urgence</label>
                            <input
                              type="text"
                              value={formData.parentEmergencyContactName}
                              onChange={(e) => handleInputChange('parentEmergencyContactName', e.target.value)}
                              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                              placeholder="Nom du contact d'urgence"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Téléphone d'urgence</label>
                            <input
                              type="tel"
                              value={formData.parentEmergencyContact}
                              onChange={(e) => handleInputChange('parentEmergencyContact', e.target.value)}
                              className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                                errors.parentEmergencyContact
                                  ? 'border-red-300 focus:border-red-500'
                                  : 'border-gray-200 focus:border-blue-500'
                              }`}
                              placeholder="+212 6 12 34 56 78"
                            />
                            {errors.parentEmergencyContact && (
                              <p className="text-xs text-red-500 mt-1">{errors.parentEmergencyContact}</p>
                            )}
                          </div>
                        </div>

                        {/* Ligne 4 : Date de naissance et Statut */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Date de naissance</label>
                            <input
                              type="date"
                              value={formData.parentDateOfBirth}
                              onChange={(e) => handleInputChange('parentDateOfBirth', e.target.value)}
                              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Statut</label>
                            <select
                              value={formData.parentStatus}
                              onChange={(e) => handleInputChange('parentStatus', e.target.value)}
                              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            >
                              <option value="active">Actif</option>
                              <option value="inactive">Inactif</option>
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </form>
            </div>

            {/* Footer */}
            <div className="px-8 py-6 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3 sticky bottom-0 z-10">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>{mode === 'create' ? "Créer l'élève" : 'Enregistrer les modifications'}</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StudentParentFormModal;
